import React, {
  createContext, useReducer, useEffect, useMemo, useContext
} from 'react';
import PropTypes, {
  bool,
  number,
  objectOf,
  arrayOf,
  string,
  node,
  shape,
  oneOfType
} from 'prop-types';

import { ExperienceContext } from '@thd-nucleus/experience-context';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';

import { calculateTotalPrice, pipe, reducerPipe, reformatPrice } from './components/utils';
import {
  TOGGLE_ALTERNATE_SELECTIONS,
  TOGGLE_PRODUCT_GROUP_SELECTION,
  RESET_STATE,
  SET_POD_INTERACTION,
  MAJOR_APPLIANCE_TYPE,
  PROMOTIONAL_ADJUSTMENTS_UPDATED,
  SWAP_PRODUCT,
  SET_PRODUCT_SELECTED_QUANTITY,
  LIMITED_STOCK_THRESHOLD,
  APPLIANCE_DEFAULT_AVAILABILITY_QUANTITY,
  STARTING_SELECTED_QUANTITY,
  REMOVE_OUT_OF_STOCK_ITEMS
} from './components/constants';

/* -------------------- */
/*    START HELPERS     */
/* -------------------- */
const determineIfMajorAppliancePackage = (apiResponsePackage) => {
  return apiResponsePackage.products?.every(
    (item) => item.identifiers.productType === MAJOR_APPLIANCE_TYPE
  );
};

const organizeProductsByClassAndSubclass = (apiResponsePackage, packageId) => {
  const newPackageModel = {
    metadata: { ...apiResponsePackage.metadata, ...{ packageId } },
    products: new Map(),
    selectedProductIds: [],
    originalItemIds: [],
    totalPricing: {},
    refetchAvailability: null,
    isMajorAppliancePackage: determineIfMajorAppliancePackage(apiResponsePackage),
    fetchPromotionalAdjustments: false,
    allProducts: {},
    alternateProducts: {},
  };

  newPackageModel.allProducts = apiResponsePackage.products?.reduce((acc, item) => {
    return { ...acc, [item.itemId]: item };
  }, {});

  const productGrouping = apiResponsePackage.products?.reduce((acc, item) => {
    const productGroupName = item.info?.productGroup;
    const alternateProduct = {
      itemId: item.identifiers.itemId,
      price: item.pricing.value,
      hasMap: item?.pricing?.mapAboveOriginalPrice !== null || item?.pricing?.mapDetail?.mapPolicy !== null,
      label: item.info?.label || item.identifiers?.productLabel
    };

    if (acc[productGroupName]) {
      acc[productGroupName].products.push(alternateProduct);
    } else {
      acc[productGroupName] = {
        products: [alternateProduct],
        default: item.identifiers.itemId
      };
    }
    return acc;
  }, {});

  return {
    newPackageModel,
    productGrouping,
    products: apiResponsePackage.products,
  };
};

export const determineSummaryDisplayPrice = ({ item, selectedQty }) => {
  const { pricing = {} } = item;
  const { mapAboveOriginalPrice, original, value, mapDetail = {} } = pricing;
  const { mapPolicy } = mapDetail || {};

  let perItemPrice;

  // mapPolicy exists
  if (mapPolicy) {
    perItemPrice = value || original;
  } else if (mapAboveOriginalPrice !== null && mapAboveOriginalPrice !== undefined) {
    perItemPrice = original;
  } else {
    perItemPrice = value;
  }

  // If perItemPrice is undefined or null, return an empty string
  if (perItemPrice == null) {
    return reformatPrice(perItemPrice);
  }

  const totalPrice = ((perItemPrice || 0) * selectedQty)?.toFixed(2);

  return reformatPrice(totalPrice);
};

const getGMAvailabilityQty = (item) => {
  const { ShipToHome, BOPIS } = podFulFillmentUtils.getAllFulfillmentInventories(item);
  const availableQty = ShipToHome || BOPIS;
  if (!availableQty) return podFulFillmentUtils.getSellableQuantityNearbyStore(item);
  return availableQty;
};

const attachAlternateProductsAndExtendProductInfo = ({
  newPackageModel,
  productGrouping,
  products
}) => {
  products.forEach((item) => {
    const productGroupName = item.info?.productGroup;
    const productGroup = productGrouping[productGroupName];
    const isSelected = productGroup?.default === item.identifiers.itemId;
    newPackageModel.originalItemIds.push(item.identifiers.itemId);

    if (isSelected) {
      newPackageModel.selectedProductIds.push(item.identifiers.itemId);
    }

    const availableQty = newPackageModel.isMajorAppliancePackage ?
      APPLIANCE_DEFAULT_AVAILABILITY_QUANTITY : getGMAvailabilityQty(item);

    const extendedItemInfo = {
      ...item,
      selected: isSelected || false,
      productGroupSelected: true,
      alternateProducts: productGroup.products.length > 1 ? productGroup : null,
      selectedQty: STARTING_SELECTED_QUANTITY,
      availableQty,
      isOutOfStock: item.fulfillment && !availableQty,
      isLimitedQuantity: item.fulfillment && availableQty <= LIMITED_STOCK_THRESHOLD,
    };
    newPackageModel.products.set(item.identifiers.itemId, extendedItemInfo);
  });
  return newPackageModel;
};

const setInitialPackageTotalPrice = (newPackageModel) => {
  return {
    ...newPackageModel,
    totalPricing: calculateTotalPrice(
      newPackageModel.selectedProductIds,
      newPackageModel.products
    )
  };
};

const updateProductGroupSelected = (product, productGroupSelected) => {
  return {
    ...product,
    productGroupSelected
  };
};

const updatePromoAdjustmentsForProduct = (oldProduct, newProduct) => {
  return {
    ...oldProduct,
    pricing: {
      ...oldProduct.pricing,
      promotionalAdjustments: newProduct?.pricing?.promotionalAdjustments || []
    }
  };
};

const clearPromotionalAdjustments = (product) => {
  return {
    ...product,
    pricing: {
      ...product.pricing,
      promotionalAdjustments: []
    }
  };
};
/* -------------------- */
/*     END HELPERS      */
/* -------------------- */

/* -------------------- */
/*    START ACTIONS     */
/* -------------------- */
const togglePodInteraction = (id) => {
  return {
    type: TOGGLE_PRODUCT_GROUP_SELECTION,
    id
  };
};

const toggleAlternateSelection = (currentId, previousId) => {
  return {
    type: TOGGLE_ALTERNATE_SELECTIONS,
    id: currentId,
    previousId
  };
};

const swapProduct = (currentItem, previousItemId, index) => {
  return {
    type: SWAP_PRODUCT,
    payload: {
      currentItem,
      previousItemId,
      index
    }
  };
};

const promotionalAdjustmentsFetched = (data) => {
  return {
    type: PROMOTIONAL_ADJUSTMENTS_UPDATED,
    payload: data
  };
};

const getSelectedIds = (products) => {
  return Array.from(products.values()).reduce((accum, item) => {
    if (item.selected && item.productGroupSelected && item.identifiers?.itemId) {
      accum.push(item.identifiers.itemId);
    }
    return accum;
  }, []);
};

const setProductSelectedQuantity = (id, selectedQty) => {
  return {
    type: SET_PRODUCT_SELECTED_QUANTITY,
    payload: {
      id,
      selectedQty
    }
  };
};

const removeOutOfStockItems = () => ({ type: REMOVE_OUT_OF_STOCK_ITEMS });
/* -------------------- */
/*      END ACTIONS     */
/* -------------------- */

/* -------------------- */
/*    START REDUCERS    */
/* -------------------- */

const updateSelectedIds = (state) => {
  const selectedProductIds = getSelectedIds(state.products);

  const selectedIsTheSame =
    selectedProductIds.length === state.selectedProductIds.length &&
    state.selectedProductIds.filter((itemId) => {
      return selectedProductIds.includes(itemId);
    }).length === state.selectedProductIds.length;

  if (selectedIsTheSame) {
    return { ...state, fetchPromotionalAdjustments: false };
  }

  return { ...state, selectedProductIds, fetchPromotionalAdjustments: true };
};

const updateTotalPricing = (state) => {
  return {
    ...state,
    totalPricing: calculateTotalPrice(
      state.selectedProductIds,
      state.products
    )
  };
};

const toggleAllItemsInProductGroup = (state, action, packageItem) => {
  if (packageItem.alternateProducts) {
    packageItem.alternateProducts.products.forEach((item) => {
      const product = state.products.get(item.itemId);
      state.products.set(
        item.itemId,
        updateProductGroupSelected(product, !product.productGroupSelected)
      );
    });
  } else {
    state.products.set(
      action.id,
      updateProductGroupSelected(packageItem, !packageItem.productGroupSelected)
    );
  }
  return state;
};

const updateAllItemsInProductGroup = (state, action, packageItem) => {
  if (packageItem.alternateProducts) {
    packageItem.alternateProducts.products.forEach((item) => {
      const product = state.products.get(item.itemId);
      state.products.set(
        item.itemId,
        updateProductGroupSelected(product, action.productGroupSelected)
      );
    });
  } else {
    state.products.set(
      action.id,
      updateProductGroupSelected(packageItem, action.productGroupSelected)
    );
  }
  return state;
};

const updateActiveItemWithinProductGroup = (state, action, packageItem) => {
  const previousItem = state.products.get(action.previousId);
  state.products.set(action.id, { ...packageItem, selected: !packageItem.selected });
  state.products.set(action.previousId, { ...previousItem, selected: !previousItem.selected });
  return state;
};

const updatePromotionalAdjustments = (state, action) => {
  const { products } = { ...state };
  /* eslint-disable-next-line no-unused-expressions */
  if (products) {
    products.forEach((product, itemId) => {
      products.set(itemId, clearPromotionalAdjustments(product));
    });
  }
  if (action && action.payload && action.payload.products) {
    /* eslint-disable-next-line no-unused-expressions */
    action.payload.products
      .filter((productWithUpdatedPromo) => products.has(productWithUpdatedPromo.itemId))
      .forEach((productWithUpdatedPromo) => {
        const product = products.get(productWithUpdatedPromo.itemId);
        products.set(
          product.itemId,
          updatePromoAdjustmentsForProduct(product, productWithUpdatedPromo)
        );
      });
  }
  return { ...state, products };
};

const attachAdditionalInfoToProduct = ({
  isMajorAppliancePackage,
  product,
  selected = true,
  alternateProducts = null,
}) => {
  const availableQty = isMajorAppliancePackage
    ? APPLIANCE_DEFAULT_AVAILABILITY_QUANTITY
    : podFulFillmentUtils.getAllFulfillmentInventories(product)?.ShipToHome;

  const isOutOfStock = product.fulfillment && !podFulFillmentUtils.isInStock(product);
  const isLimitedQuantity = availableQty <= LIMITED_STOCK_THRESHOLD;

  return {
    ...product,
    selected,
    productGroupSelected: true,
    selectedQty: STARTING_SELECTED_QUANTITY,
    availableQty,
    isOutOfStock,
    isLimitedQuantity,
    alternateProducts,
  };
};

const updateSwappedProduct = (state, action) => {
  const { products } = { ...state };
  const { currentItem, previousItemId } = action?.payload || {};
  const newProducts = new Map();

  // ItemIds for the previous item's alternate products
  const previousItemAlternateIds = products.get(previousItemId)
    .alternateProducts?.products?.map(({ itemId }) => itemId) || [];

  products.forEach((product, itemId) => {
    // Don't include previous item or previous item's alternate products in new list
    if (itemId !== previousItemId && !previousItemAlternateIds.includes(itemId)) {
      newProducts.set(itemId, product);
    } else {
      // Apply original alternateProducts if we swap back to an item with alternateProducts
      const alternateProducts = state.alternateProducts[currentItem.itemId] || null;

      const newProduct = attachAdditionalInfoToProduct({
        product: currentItem,
        alternateProducts,
        isMajorAppliancePackage: state.isMajorAppliancePackage,
      });

      newProducts.set(currentItem.itemId, newProduct);

      // If we have alternateProducts we need to add them back into the products Map
      if (alternateProducts) {
        alternateProducts.products.forEach(({ itemId: altId }) => {
          if (altId !== currentItem.itemId) {
            newProducts.set(altId, attachAdditionalInfoToProduct({
              product: state.allProducts[altId],
              alternateProducts,
              isMajorAppliancePackage: state.isMajorAppliancePackage,
              selected: false,
            }));
          }
        });
      }
    }
  });

  return {
    ...state,
    products: newProducts,
    alternateProducts: {
      ...state.alternateProducts,
      // Save current alternateProducts to state.
      [previousItemId]: products.get(previousItemId).alternateProducts || null,
    }
  };
};

const updatePreviouslySelectedItem = (state, action) => {
  const { previousItemId, currentItem } = action.payload;

  const packagePodMessages = {
    ...state.packagePodMessages,
    [currentItem.itemId]: {
      previousItemId,
    }
  };

  return { ...state, packagePodMessages };
};

const updateAllProducts = (state, action) => {
  const { currentItem } = action.payload;
  const allProducts = {
    ...state.allProducts,
    [currentItem.itemId]: currentItem,
  };

  return { ...state, allProducts };
};

const updateSelectedQuantityAndPricing = (state, action) => {
  const newProducts = new Map(Array.from(state.products));
  const productToUpdate = state.products.get(action.payload.id);

  newProducts.set(productToUpdate.itemId, {
    ...productToUpdate,
    selectedQty: action.payload.selectedQty,
  });

  return { ...state, products: newProducts };
};

const updateProductsWithOOSRemoval = (state) => {
  const newProducts = new Map(state.products);
  const newProductsState = { products: newProducts };
  const outOfStockIds = [];

  state.products.forEach((product) => {
    const { itemId, isOutOfStock } = product;

    if (state.selectedProductIds.includes(itemId) && isOutOfStock) {
      updateAllItemsInProductGroup(newProductsState, { id: itemId, productGroupSelected: false }, product);
      outOfStockIds.push(itemId);
    }
  });

  const selectedProductIds = state.selectedProductIds.filter((value) => !outOfStockIds.includes(value));

  return { ...state, products: newProductsState.products, selectedProductIds };
};
/* -------------------- */
/*     END REDUCERS     */
/* -------------------- */

const packagesReducer = (state, action) => {
  const packageItem = state.products.get(action.id);
  switch (action.type) {
  case TOGGLE_PRODUCT_GROUP_SELECTION:
    return {
      ...reducerPipe(toggleAllItemsInProductGroup, updateSelectedIds)(state, action, packageItem)
    };
  case SET_POD_INTERACTION:
    return {
      ...pipe(updateAllItemsInProductGroup, updateSelectedIds)(state, action, packageItem)
    };
  case TOGGLE_ALTERNATE_SELECTIONS:
    return {
      ...pipe(updateActiveItemWithinProductGroup, updateSelectedIds)(state, action, packageItem)
    };
  case RESET_STATE:
    return {
      ...action.newState,
    };
  case PROMOTIONAL_ADJUSTMENTS_UPDATED:
    return {
      ...reducerPipe(updatePromotionalAdjustments, updateTotalPricing)(state, action)
    };
  case SWAP_PRODUCT:
    return {
      ...reducerPipe(
        updateSwappedProduct,
        updatePreviouslySelectedItem,
        updateAllProducts,
        updateSelectedIds,
        updateTotalPricing,
      )(state, action)
    };
  case SET_PRODUCT_SELECTED_QUANTITY: {
    return {
      ...reducerPipe(
        updateSelectedQuantityAndPricing,
        updateTotalPricing
      )(state, action)
    };
  }
  case REMOVE_OUT_OF_STOCK_ITEMS:
    return {
      ...reducerPipe(updateProductsWithOOSRemoval, updateSelectedIds, updateTotalPricing)(state)
    };
  default:
    return state;
  }
};

// TODO: Context should have an actual default value
export const OrderAPackageContext = createContext({ state: {} });
export const useOrderAPackageContext = () => useContext(OrderAPackageContext);

export const OrderAPackageProvider = ({
  data, loading, error, children, packageId, isCustomerIdentified
}) => {

  const restructuredPackageData = useMemo(() => {
    return pipe(
      organizeProductsByClassAndSubclass,
      attachAlternateProductsAndExtendProductInfo,
      setInitialPackageTotalPrice
    )(data.packages[0], packageId);
  }, [data, packageId]);

  const { channel } = useContext(ExperienceContext);
  const [state, dispatch] = useReducer(packagesReducer, restructuredPackageData);

  const fulfillmentLoaded = 'fulfillment' in data.packages[0].products[0];

  useEffect(() => {
    dispatch({ type: RESET_STATE, newState: restructuredPackageData });
  }, [restructuredPackageData]);

  const contextValue = {
    channel,
    state,
    loading,
    error,
    dispatch,
    actions: {
      toggleAlternateSelection,
      togglePodInteraction,
      promotionalAdjustmentsFetched,
      swapProduct,
      setProductSelectedQuantity,
      removeOutOfStockItems
    },
    fulfillmentLoaded,
    isCustomerIdentified
  };

  if (!restructuredPackageData) return null;

  return (
    <OrderAPackageContext.Provider value={contextValue}>{children}</OrderAPackageContext.Provider>
  );
};

OrderAPackageProvider.propTypes = {
  data: shape({
    metadata: oneOfType([PropTypes.object, string]),
    products: objectOf(Map),
    selectedProductIds: arrayOf(string),
    totalPricing: objectOf(oneOfType([number, string, PropTypes.object, bool])),
    isMajorAppliancePackage: bool,
    fetchPromotionalAdjustments: bool
  }).isRequired,
  packageId: string.isRequired,
  loading: bool.isRequired,
  /* eslint-disable-next-line react/forbid-prop-types */
  error: PropTypes.object,
  children: node.isRequired,
  isCustomerIdentified: bool.isRequired
};

OrderAPackageProvider.defaultProps = {
  error: null
};
